import React, { useEffect, useState } from "react";
import { Modal, Table, Button, Spin, message } from "antd";
import {
  DownloadOutlined,
  FilePdfOutlined,
} from "@ant-design/icons";
import { useGetFormStatsForExportQuery } from "../../store/api/apiSlice";
import { jsPDF } from "jspdf";
import { applyPlugin } from "jspdf-autotable";

applyPlugin(jsPDF);
import {
  getUserAvatarBackground,
  getUserAvatarBackgroundByIndex,
  getRespondentLabelIndex,
  compareLocaleStrings,
  compareNumericValues,
  buildLongFormatRecords,
  buildMatrixPdfColumnStyles,
  buildMatrixTableCsvRows,
  buildExampleFormatCsvRows,
  buildQuestionNumberMap,
  buildRespondentPseudonymMap,
  getInternalRespondentKey,
  compareLongFormatRecords,
  formatMatrixExportValue,
  formatCheckboxExportValue,
  stringifyChoiceOption,
  isZeroBasedScaleOptions,
  getSliderStarRating,
  EMPTY_EXPORT_VALUE,
  UNTRANSCRIBED_VIDEO_VALUE,
  extractMediaUrl,
  extractTranscriptionText,
  getFileResponseUrls,
  serializeMediaForExport,
  isMediaInputType,
  isMediaStorageUrl,
  isUsableTranscriptionText,
  // countMissingRecordVideoTranscriptions,
  extractSliderNumericValue,
  normalizeCheckboxOptions,
  getCheckboxSelections,
  formatExportInputTypeLabel,
  isAnonymousExportResponse,
  isAnonymousExportInput,
  isConsentReportField,
} from "../../utils/reportsHelper";
import {
  formatRadioListDisplayValue,
  getRadioListDisplayLines,
  isRadioListInputType,
} from "../../utils/radioList";
import { buildConditionalPagination } from "../../utils/pagination";
import {
  DEFAULT_EMOJI_DESCRIPTIONS,
  EMOJI_SETS,
} from "../../constants/reportPDFs";
import UserGradientAvatar from "../common/UserGradientAvatar";
import FileResponsePreview from "../common/FileResponsePreview";
import { pickMediaUrl } from "../../utils/formTabs";
import { getEnvironmentConfig } from "../../config/environment";
import { isImageFileUrl, normalizeFileUrls } from "../../utils/filePreview";

const formatRateToTwoDecimals = (value) => {
  if (value == null || value === "") return "0.0";

  const raw = String(value).trim();
  const hasPercent = raw.includes("%");
  const numeric = Number.parseFloat(raw.replace("%", "").replace(/,/g, ""));

  if (!Number.isFinite(numeric)) return raw;

  return `${numeric.toFixed(1)}${hasPercent ? "%" : ""}`;
};

const getExportResponseRatio = (stats) => {
  const apiRatio = Number(
    stats?.response_ratio ?? stats?.response_rate ?? stats?.responseRate ?? 0,
  );

  return Number.isFinite(apiRatio) ? apiRatio.toFixed(1) : "0.0";
};

const drawCanvasStar = (ctx, cx, cy, radius, filled) => {
  const points = 5;
  const inner = radius * 0.45;
  ctx.beginPath();

  for (let i = 0; i < points * 2; i += 1) {
    const r = i % 2 === 0 ? radius : inner;
    const angle = -Math.PI / 2 + (i * Math.PI) / points;
    const x = cx + r * Math.cos(angle);
    const y = cy + r * Math.sin(angle);
    if (i === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  }

  ctx.closePath();
  ctx.fillStyle = filled ? "#FFD700" : "#FFFFFF";
  ctx.strokeStyle = filled ? "#F5B700" : "#B1B1B1";
  ctx.lineWidth = Math.max(1.5, radius * 0.08);
  ctx.fill();
  ctx.stroke();
};

const LOGO_PDF_PIXEL_SIZE = 256;
const LOGO_PDF_DRAW_MM = 6;
const EMOJI_PDF_PIXEL_SIZE = 256;
const EMOJI_PDF_DRAW_MM = 5;
const STAR_PDF_PIXEL_SIZE = 64;
const STAR_PDF_DRAW_MM = 4.5;
const FILE_IMAGE_PDF_DRAW_MM = 6;

const composeStarsPdfImage = (filledCount, totalCount) => {
  if (typeof document === "undefined") return null;

  const total = Math.max(Number(totalCount) || 0, 1);
  const filled = Math.min(Math.max(Number(filledCount) || 0, 0), total);
  const starSize = STAR_PDF_PIXEL_SIZE;
  const gap = Math.round(starSize * 0.18);
  const padding = Math.round(starSize * 0.08);

  const canvas = document.createElement("canvas");
  canvas.width = total * (starSize + gap) - gap + padding * 2;
  canvas.height = starSize + padding * 2;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;

  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";

  for (let i = 0; i < total; i += 1) {
    drawCanvasStar(
      ctx,
      padding + i * (starSize + gap) + starSize / 2,
      canvas.height / 2,
      starSize / 2.35,
      i < filled,
    );
  }

  return {
    dataUrl: canvas.toDataURL("image/png"),
    width: canvas.width,
    height: canvas.height,
  };
};

const composeEmojiPdfImage = (symbol, labelText = "") => {
  if (!symbol || typeof document === "undefined") return null;

  const size = EMOJI_PDF_PIXEL_SIZE;
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;

  ctx.clearRect(0, 0, size, size);
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  // Large glyph → downscale in PDF for sharp edges.
  ctx.font = `${Math.floor(size * 0.78)}px "Segoe UI Emoji", "Apple Color Emoji", "Noto Color Emoji", "Twemoji Mozilla", sans-serif`;
  ctx.fillText(String(symbol), size / 2, size / 2 + size * 0.04);

  return {
    dataUrl: canvas.toDataURL("image/png"),
    width: size,
    height: size,
    label: String(labelText || "").trim(),
  };
};

const renderPdfRatingVisual = (visual) => {
  if (!visual || typeof document === "undefined") return null;

  if (visual.rendered) {
    return visual.rendered;
  }

  if (visual.type === "stars") {
    return composeStarsPdfImage(visual.filled, visual.total);
  }

  if (visual.type === "emoji" && visual.symbol) {
    return composeEmojiPdfImage(visual.symbol, visual.label);
  }

  return null;
};

const drawImageObjectCover = (ctx, img, size) => {
  const sourceWidth = img.naturalWidth || img.width || size;
  const sourceHeight = img.naturalHeight || img.height || size;
  const scale = Math.max(size / sourceWidth, size / sourceHeight);
  const drawWidth = sourceWidth * scale;
  const drawHeight = sourceHeight * scale;
  const offsetX = (size - drawWidth) / 2;
  const offsetY = (size - drawHeight) / 2;
  ctx.drawImage(img, offsetX, offsetY, drawWidth, drawHeight);
};

const composeLogoPdfImage = (logoDataUrl, labelText = "") =>
  new Promise((resolve) => {
    if (!logoDataUrl || typeof document === "undefined") {
      resolve(null);
      return;
    }

    const size = LOGO_PDF_PIXEL_SIZE;
    const canvas = document.createElement("canvas");
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      resolve(null);
      return;
    }

    const img = new Image();
    img.onload = () => {
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = "high";

      // Match survey: white circular badge with border + object-cover logo.
      ctx.beginPath();
      ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2);
      ctx.closePath();
      ctx.fillStyle = "#FFFFFF";
      ctx.fill();

      ctx.save();
      ctx.beginPath();
      ctx.arc(size / 2, size / 2, size / 2 - 2, 0, Math.PI * 2);
      ctx.closePath();
      ctx.clip();
      drawImageObjectCover(ctx, img, size);
      ctx.restore();

      ctx.beginPath();
      ctx.arc(size / 2, size / 2, size / 2 - 1.5, 0, Math.PI * 2);
      ctx.strokeStyle = "#E5E7EB";
      ctx.lineWidth = Math.max(2, size * 0.02);
      ctx.stroke();

      resolve({
        dataUrl: canvas.toDataURL("image/png"),
        width: size,
        height: size,
        label: String(labelText || "").trim(),
      });
    };
    img.onerror = () => resolve(null);
    img.src = logoDataUrl;
  });

const blobToDataUrl = (blob) =>
  new Promise((resolve) => {
    try {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    } catch {
      resolve(null);
    }
  });

const extractStorageKeyFromUrl = (url) => {
  try {
    const pathname = new URL(url).pathname || "";
    return decodeURIComponent(pathname.split("/").filter(Boolean).pop() || "");
  } catch {
    const parts = String(url || "").split("/");
    return decodeURIComponent(parts[parts.length - 1] || "");
  }
};

const fetchUrlAsDataUrl = async (url) => {
  const response = await fetch(url, { mode: "cors" });
  if (!response.ok) {
    throw new Error(`logo fetch failed: ${response.status}`);
  }
  return blobToDataUrl(await response.blob());
};

const loadImageAsDataUrl = async (url) => {
  const sourceUrl = pickMediaUrl(url);
  if (!sourceUrl || typeof document === "undefined") {
    return null;
  }

  // 1) Direct CORS fetch
  try {
    const dataUrl = await fetchUrlAsDataUrl(sourceUrl);
    if (dataUrl) return dataUrl;
  } catch {
    // continue
  }

  // 2) Backend file lookup → signed/public URL (same-origin API has CORS)
  try {
    const key = extractStorageKeyFromUrl(sourceUrl);
    if (key) {
      const { baseURL } = getEnvironmentConfig();
      const token = localStorage.getItem("token");
      const headers = {};
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const metaResponse = await fetch(
        `${baseURL}/file/file/${encodeURIComponent(key)}`,
        {
          method: "GET",
          credentials: "include",
          headers,
        },
      );

      if (metaResponse.ok) {
        const meta = await metaResponse.json();
        const fileUrl = pickMediaUrl(
          meta?.file || meta?.data?.file || meta?.url || meta?.data?.url,
        );
        if (fileUrl) {
          try {
            const dataUrl = await fetchUrlAsDataUrl(fileUrl);
            if (dataUrl) return dataUrl;
          } catch {
            // continue
          }
        }
      }
    }
  } catch {
    // continue
  }

  // 3) Public image proxy (S3 bucket has no CORS headers for canvas/PDF use)
  try {
    if (/^https?:\/\//i.test(sourceUrl)) {
      const proxied = `https://wsrv.nl/?url=${encodeURIComponent(sourceUrl)}&w=512&h=512&fit=cover&output=png&q=100&n=-100`;
      const dataUrl = await fetchUrlAsDataUrl(proxied);
      if (dataUrl) return dataUrl;
    }
  } catch {
    // continue
  }

  // 4) Last attempt via Image + canvas
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth || 64;
        canvas.height = img.naturalHeight || 64;
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          resolve(null);
          return;
        }
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL("image/png"));
      } catch {
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = sourceUrl;
  });
};

const parseChoiceOptionLabels = (inputOptions) => {
  if (inputOptions == null || inputOptions === "") return [];

  if (Array.isArray(inputOptions)) {
    if (
      inputOptions.length === 1 &&
      typeof inputOptions[0] === "string" &&
      (/[,\n]/.test(inputOptions[0]) || inputOptions[0].includes("|"))
    ) {
      return String(inputOptions[0])
        .split(/[,|\n]/)
        .map((option) => option.trim())
        .filter(Boolean);
    }

    return inputOptions
      .map((option) => stringifyChoiceOption(option))
      .filter(Boolean);
  }

  if (typeof inputOptions === "string") {
    return inputOptions
      .split(/[,|\n]/)
      .map((option) => option.trim())
      .filter(Boolean);
  }

  return [];
};

const buildChoiceLogoPdfConfigByQuestionId = (formQuestions = []) => {
  const map = new Map();
  const questionNumberMap = buildQuestionNumberMap(formQuestions);

  (formQuestions || []).forEach((question) => {
    const exportNumber =
      questionNumberMap[question.number] ?? question.number;
    if (exportNumber == null || exportNumber === "") return;

    const questionId = `q${exportNumber}`.toLowerCase();
    const configs = [];

    (question.response || []).forEach((field) => {
      const inputType = String(field.input_type || "")
        .trim()
        .toLowerCase();

      if (inputType !== "radio" && inputType !== "checkbox") {
        return;
      }

      const logos = Array.isArray(field.option_logos) ? field.option_logos : [];
      const normalizedLogos = logos.map((entry) => pickMediaUrl(entry));
      const hasLogos = normalizedLogos.some((logoUrl) => Boolean(logoUrl));
      const showLogos = Boolean(field.show_logos) || hasLogos;

      if (!showLogos || !hasLogos) {
        return;
      }

      const options = parseChoiceOptionLabels(field.input_options);
      if (options.length === 0) return;

      configs.push({
        inputType,
        options,
        logos: options.map((_, index) => normalizedLogos[index] || ""),
      });
    });

    if (configs.length > 0) {
      map.set(questionId, configs);
    }
  });

  return map;
};

const buildChoiceLogoUrlByOption = (choiceLogoConfigByQuestionId) => {
  const byOption = new Map();

  choiceLogoConfigByQuestionId.forEach((configs) => {
    (configs || []).forEach((config) => {
      (config.options || []).forEach((option, index) => {
        const optionKey = String(option || "")
          .trim()
          .toLowerCase();
        const logoUrl = pickMediaUrl(config.logos?.[index]);
        if (optionKey && logoUrl && !byOption.has(optionKey)) {
          byOption.set(optionKey, logoUrl);
        }
      });
    });
  });

  return byOption;
};

const resolveChoiceLogoPdfVisual = (
  configs,
  inputType,
  optionLabel,
  displayLabel,
) => {
  if (!Array.isArray(configs) || configs.length === 0) return null;

  const optionKey = String(optionLabel || "")
    .trim()
    .toLowerCase();
  if (!optionKey || optionKey === "other") return null;

  const normalizedType = String(inputType || "")
    .trim()
    .toLowerCase();

  const orderedConfigs = [
    ...configs.filter((config) => config.inputType === normalizedType),
    ...configs.filter((config) => config.inputType !== normalizedType),
  ];

  for (const config of orderedConfigs) {
    const index = config.options.findIndex(
      (option) => String(option).trim().toLowerCase() === optionKey,
    );

    if (index < 0) continue;

    const logoUrl = pickMediaUrl(config.logos[index]);
    if (!logoUrl) continue;

    return {
      type: "logo",
      logoUrl,
      label: String(displayLabel || optionLabel || "").trim(),
    };
  }

  return null;
};

const buildSliderPdfConfigByQuestionId = (formQuestions = []) => {
  const map = new Map();
  const questionNumberMap = buildQuestionNumberMap(formQuestions);

  (formQuestions || []).forEach((question) => {
    const exportNumber =
      questionNumberMap[question.number] ?? question.number;
    if (exportNumber == null) return;

    const sliderInput = (question.response || []).find(
      (field) =>
        String(field.input_type || "")
          .trim()
          .toLowerCase() === "slider",
    );

    if (!sliderInput) return;

    const options = Array.isArray(sliderInput.input_options)
      ? sliderInput.input_options
          .map((option) => String(option ?? "").trim())
          .filter(Boolean)
      : Array.isArray(sliderInput.slider_config?.options)
        ? sliderInput.slider_config.options
            .map((option) => String(option ?? "").trim())
            .filter(Boolean)
        : [];

    const totalOptions = Math.max(
      options.length ||
        Number(sliderInput.slider_config?.scale_points) ||
        parseInt(sliderInput.scale_type || "5", 10) ||
        5,
      1,
    );

    map.set(`q${exportNumber}`, {
      showStars: Boolean(
        sliderInput.show_stars || sliderInput.slider_config?.show_stars,
      ),
      showEmojis: Boolean(
        sliderInput.show_emojis ||
          sliderInput.slider_config?.show_emojis ||
          sliderInput.show_only_emojis ||
          sliderInput.slider_config?.show_only_emojis,
      ),
      showOnlyEmojis: Boolean(
        sliderInput.show_only_emojis ||
          sliderInput.slider_config?.show_only_emojis,
      ),
      zeroBased: isZeroBasedScaleOptions(options),
      options,
      totalOptions,
    });
  });

  return map;
};

const resolveSliderPdfVisual = (config, answerText) => {
  if (!config || answerText == null) return null;

  const answer = String(answerText).trim();
  if (!answer) return null;

  const { options, totalOptions, showStars, showEmojis, showOnlyEmojis, zeroBased } =
    config;

  let index = options.findIndex(
    (option) => option.toLowerCase() === answer.toLowerCase(),
  );

  if (index < 0 && /^\d+(\.\d+)?$/.test(answer)) {
    const numeric = Number(answer);
    if (Number.isInteger(numeric)) {
      if (numeric >= 1 && numeric <= totalOptions) {
        index = numeric - 1;
      } else if (zeroBased && numeric >= 0 && numeric < totalOptions) {
        index = numeric;
      }
    }
  }

  if (index < 0) {
    const descriptions =
      DEFAULT_EMOJI_DESCRIPTIONS[String(totalOptions)] ||
      DEFAULT_EMOJI_DESCRIPTIONS["5"] ||
      [];
    index = descriptions.findIndex(
      (label) =>
        String(label).trim().toLowerCase() === answer.toLowerCase(),
    );
  }

  if (index < 0) return null;

  if (showStars || zeroBased) {
    return {
      type: "stars",
      filled: index + 1,
      total: totalOptions,
    };
  }

  if (showEmojis) {
    const emojiSet = EMOJI_SETS[String(totalOptions)] || EMOJI_SETS["5"];
    const symbol = emojiSet?.[index]?.symbol;
    if (!symbol) return null;

    return {
      type: "emoji",
      symbol,
      label: showOnlyEmojis ? null : answer,
    };
  }

  return null;
};

const ExportReportFormsModal = ({ visible, onClose, exportData }) => {
  const [loading, setLoading] = useState(false);
  const [tableData, setTableData] = useState([]);

  const { data: statsData, isLoading: isLoadingStats } =
    useGetFormStatsForExportQuery(exportData?.formObj?._id, {
      skip: !exportData?.formObj?._id,
    });

  useEffect(() => {
    if (visible && exportData?.reportData) {
      parseReportData();
    }
  }, [visible, exportData]);

  const getEmoji = (index, totalOptions = 5) => {
    const emojiSet = EMOJI_SETS[totalOptions.toString()] || EMOJI_SETS["5"];
    const emojiData = emojiSet[index] || emojiSet[0];

    return {
      Icon: emojiData.Icon,
      color: emojiData.color,
      symbol: emojiData.symbol,
    };
  };

  const capitalizeFirstLetter = (string) => {
    if (!string || typeof string !== "string") return string;

    return string;
  };

  const capitalizeTypeFirstLetter = (string) => {
    if (!string || typeof string !== "string") return string;

    return string
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const formatPdfColumnTitle = (header) => {
    if (!header || typeof header !== "string") return header;

    return header
      .replace(/_/g, " ")
      .split(" ")
      .filter(Boolean)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  const getMatrixExportValue = (inputField, inputResponse) =>
    formatMatrixExportValue(inputField, inputResponse);

  const resolveFieldOptions = (inputField = {}) => {
    const candidates = [
      inputField.input_options,
      inputField.slider_config?.options,
      inputField.options,
    ];

    for (const candidate of candidates) {
      if (Array.isArray(candidate) && candidate.length > 0) {
        return normalizeCheckboxOptions(candidate);
      }

      if (typeof candidate === "string" && candidate.trim()) {
        return normalizeCheckboxOptions(candidate);
      }
    }

    return [];
  };

  const userMetaById = new Map();

  const getUserDisplayName = (user, index) => {
    const fullName = user?.full_name;

    if (typeof fullName === "string" && fullName.trim()) {
      return fullName.trim();
    }

    if (typeof index === "number" && index >= 0) {
      return `User${String(index + 1).padStart(3, "0")}`;
    }

    return null;
  };

  const resolveUserInfo = (item, parsedResponse) => {
    const user = item?.user || {};
    const userId = user?._id ? String(user._id) : null;

    if (userId && userMetaById.has(userId)) {
      return userMetaById.get(userId);
    }

    let userEmail = user?.email;
    let userName = getUserDisplayName(user);

    if (parsedResponse?.responses) {
      Object.values(parsedResponse.responses).forEach((response) => {
        if (!response || typeof response !== "object") return;

        const value =
          response.value != null && typeof response.value !== "object"
            ? String(response.value).trim()
            : "";

        if (!value) return;

        if (!userEmail && response.type === "email") {
          userEmail = value;
        }

        if (!userName && response.type === "name") {
          userName = value;
        }
      });
    }

    userEmail =
      typeof userEmail === "string" && userEmail.trim()
        ? userEmail.trim()
        : null;

    const avatar = user?.image || user?.avatar || null;
    const avatarBackground = getUserAvatarBackground(userId || userEmail);
    const userInfo = {
      userName,
      userEmail,
      avatar,
      userId,
      avatarBackground,
    };

    if (userId) {
      userMetaById.set(userId, userInfo);
    }

    return userInfo;
  };

  const formatResponses = (reportData, questionNumberMapOverride = null) => {
    if (!reportData || !Array.isArray(reportData)) return [];

    userMetaById.clear();

    const questionNumberMap =
      questionNumberMapOverride ||
      (() => {
        const uniqueQuestionNumbers = [
          ...new Set(
            reportData
              .filter((item) => item.question?.number != null)
              .map((item) => item.question.number),
          ),
        ].sort((a, b) => Number(a) - Number(b));

        const map = {};
        uniqueQuestionNumbers.forEach((num, index) => {
          map[num] = index + 1;
        });

        return map;
      })();

    const responses = [];

    reportData.forEach((item, responseIndex) => {
      try {
        if (!item.question || !item.response) return;

        const question = item.question;
        const rawAssignments = Array.isArray(item.response_assignments)
          ? item.response_assignments
          : [];
        const questionId = item.questionId || question?._id;
        const matchedAssignments = rawAssignments.filter((assignment) => {
          if (!assignment || typeof assignment !== "object") return false;

          const assignmentQuestion =
            assignment.question?._id || assignment.question;

          if (!assignmentQuestion) return true;

          return (
            String(assignmentQuestion) === String(questionId) ||
            String(assignmentQuestion) === String(question?._id) ||
            String(assignmentQuestion) === String(question?.number)
          );
        });
        const responseAssignments =
          matchedAssignments.length > 0
            ? matchedAssignments
            : rawAssignments.length <= 1
              ? rawAssignments
              : [];
        const responseAssignment = responseAssignments[0];
        const qNum =
          question.number != null
            ? (questionNumberMap[question.number] ?? question.number)
            : responseIndex + 1;

        let parsedResponse;

        try {
          parsedResponse =
            typeof item.response === "string"
              ? JSON.parse(item.response)
              : item.response;
        } catch (e) {
          console.error("Error parsing response JSON:", e);
          responses.push({
            key: `error-${responseIndex}`,
            name: "Error",
            email: "N/A",
            avatar: null,
            questionNumber: qNum,
            questionTitle: question.title || "N/A",
            type: "Error",
            userResponse: "Error parsing response",
            rawData: item,
          });
          return;
        }

        const questionTranscriptionText =
          extractTranscriptionText({
            transcription:
              item.transcriptionText ||
              question?.transcriptionText ||
              parsedResponse?.transcriptionText ||
              null,
            translation:
              item.translation ||
              question?.translation ||
              parsedResponse?.translation,
            assignments: responseAssignments,
          }) || null;

        const { userName, userEmail, avatar, userId, avatarBackground } =
          resolveUserInfo(item, parsedResponse);

        if (
          !question.response ||
          !Array.isArray(question.response) ||
          question.response.length === 0
        ) {
          responses.push({
            key: `${item._id}-${responseIndex}`,
            name: userName,
            email: userEmail,
            avatar,
            userId,
            avatarBackground,
            questionNumber: qNum,
            questionTitle: question.title || "N/A",
            type: "Unknown",
            userResponse: parsedResponse.responses
              ? JSON.stringify(parsedResponse.responses)
              : "No response format",
            rawData: item,
          });
          return;
        }

        const usedResponseKeys = new Set();
        const responseInputs = [];

        question.response.forEach((inputField, inputIndex) => {
          const responseValues = parsedResponse.responses || {};

          const responseTypeMatchesField = (responseType, fieldType) => {
            const response = String(responseType || "")
              .trim()
              .toLowerCase();
            const field = String(fieldType || "")
              .trim()
              .toLowerCase();

            if (!response || !field) return false;
            if (response === field) return true;
            if (field === "record-video" && response === "video") return true;
            if (field === "paragraph" && response === "text") return true;
            if (field === "text" && response === "paragraph") return true;
            if (field === "name" && response === "text") return true;
            if (field === "email" && response === "text") return true;

            return false;
          };

          const isSliderShapedValue = (value) => {
            if (value == null || value === "") return false;
            if (typeof value === "number" && !Number.isNaN(value)) return true;
            if (typeof value === "string") {
              const trimmed = value.trim();

              if (trimmed === "") return false;

              if (/^\d+(\.\d+)?$/.test(trimmed)) return true;

              return false;
            }

            if (typeof value === "object") {
              return (
                value.value != null ||
                value.emojiIndex != null ||
                value.label != null ||
                value.selection != null
              );
            }

            return false;
          };

          let questionKey = Object.keys(responseValues).find(
            (key) =>
              responseValues[key] &&
              String(responseValues[key].inputId) === String(inputField._id),
          );

          if (!questionKey) {
            const questionKeysWithIndex = Object.keys(responseValues).filter(
              (key) => {
                const keyParts = key.split("_");
                const entry = responseValues[key];

                if (
                  !entry ||
                  usedResponseKeys.has(key) ||
                  !key.includes(question._id) ||
                  keyParts.length <= 2 ||
                  isNaN(parseInt(keyParts[2], 10))
                ) {
                  return false;
                }

                if (
                  entry.type &&
                  !responseTypeMatchesField(entry.type, inputField.input_type)
                ) {
                  return false;
                }

                if (
                  inputField.input_type === "slider" &&
                  !isSliderShapedValue(entry.value)
                ) {
                  return false;
                }

                return true;
              },
            );

            if (questionKeysWithIndex.length > 0) {
              questionKeysWithIndex.sort((a, b) => {
                const indexA = parseInt(a.split("_")[2], 10);
                const indexB = parseInt(b.split("_")[2], 10);
                return indexA - indexB;
              });

              const fieldIndex = question.response.findIndex(
                (field) => field._id === inputField._id,
              );

              const matchingKey = questionKeysWithIndex.find((key) => {
                const keyIndex = parseInt(key.split("_")[2], 10);
                return keyIndex === fieldIndex;
              });

              if (matchingKey) {
                questionKey = matchingKey;
              } else if (questionKeysWithIndex.length === 1) {
                questionKey = questionKeysWithIndex[0];
              }
            }
          }

          if (!questionKey) {
            questionKey = Object.keys(responseValues).find((key) => {
              if (usedResponseKeys.has(key)) return false;

              const entry = responseValues[key];

              if (!entry) return false;

              if (String(entry.inputId) === String(inputField._id)) {
                return true;
              }

              const includesQuestionId = key.includes(question._id);
              if (!includesQuestionId) return false;

              if (
                !responseTypeMatchesField(entry.type, inputField.input_type)
              ) {
                return false;
              }

              if (
                inputField.input_type === "slider" &&
                !isSliderShapedValue(entry.value)
              ) {
                return false;
              }

              return true;
            });
          }

          if (questionKey) {
            usedResponseKeys.add(questionKey);
          }

          const inputResponse = questionKey
            ? responseValues[questionKey]
            : null;

          if (
            !inputResponse ||
            inputResponse.value == null ||
            inputResponse.value === ""
          ) {
            responseInputs.push({
              key: `${item._id}-${responseIndex}-${inputIndex}`,
              title: inputField.input_title || `Input ${inputIndex + 1}`,
              inputTitle: inputField.input_title || null,
              type: inputResponse?.type || inputField.input_type || "Text",
              fieldType: inputField.input_type || null,
              inputId: inputField._id || null,
              userResponse: EMPTY_EXPORT_VALUE,
              sliderInfo: null,
              rawValue: null,
              inputOptions: resolveFieldOptions(inputField),
              input_options: resolveFieldOptions(inputField),
              slider_config: inputField.slider_config || null,
              mediaUrl: null,
              transcriptionText: null,
              exportValue: EMPTY_EXPORT_VALUE,
            });
            return;
          }

          const responseType =
            inputResponse.type || inputField.input_type || "Text";
          const fieldOptions = resolveFieldOptions(inputField);

          let formattedResponse = EMPTY_EXPORT_VALUE;
          let sliderInfo = null;

          switch (inputField.input_type) {
            case "checkbox": {
              formattedResponse = formatCheckboxExportValue(
                inputResponse.value,
              );
              break;
            }
            case "file":
            case "multiple-files": {
              const fileUrls = getFileResponseUrls(inputResponse.value);
              formattedResponse = fileUrls.length
                ? ` ${fileUrls.join(", ")}`
                : EMPTY_EXPORT_VALUE;
              break;
            }
            case "record-video": {
              const mediaUrl = extractMediaUrl(inputResponse.value);
              const transcriptionText = extractTranscriptionText({
                transcription:
                  inputResponse?.transcriptionText ||
                  inputResponse?.transcription ||
                  parsedResponse?.transcriptionText ||
                  questionTranscriptionText,
                translation:
                  inputResponse?.translation ||
                  inputResponse?.value?.translation ||
                  parsedResponse?.translation ||
                  responseAssignment?.translation,
                assignments: responseAssignments,
                rawValue: inputResponse.value,
              });
              const hasMedia = Boolean(mediaUrl);
              const mediaExportValue = transcriptionText
                ? transcriptionText
                : serializeMediaForExport({
                    mediaUrl,
                    transcription: null,
                    hasMedia,
                  });

              if (transcriptionText) {
                formattedResponse = transcriptionText;
              } else if (hasMedia) {
                formattedResponse = UNTRANSCRIBED_VIDEO_VALUE;
              } else {
                formattedResponse = EMPTY_EXPORT_VALUE;
              }

              responseInputs.push({
                key: `${item._id}-${responseIndex}-${inputIndex}`,
                title: inputField.input_title || `Input ${inputIndex + 1}`,
                inputTitle: inputField.input_title || null,
                type: responseType,
                fieldType: inputField.input_type || null,
                inputId: inputField._id || null,
                userResponse: formattedResponse,
                sliderInfo: null,
                rawValue: inputResponse.value,
                inputOptions: fieldOptions,
                input_options: fieldOptions,
                slider_config: inputField.slider_config || null,
                mediaUrl,
                transcriptionText,
                translation:
                  inputResponse?.translation || responseAssignment?.translation,
                response_assignments: responseAssignments,
                exportValue: mediaExportValue,
              });
              return;
            }
            case "video":
            case "audio":
            case "media": {
              const mediaUrl = extractMediaUrl(inputResponse.value);
              const transcriptionText = extractTranscriptionText({
                transcription:
                  inputResponse?.transcriptionText ||
                  inputResponse?.transcription ||
                  parsedResponse?.transcriptionText ||
                  questionTranscriptionText,
                translation:
                  inputResponse?.translation ||
                  inputResponse?.value?.translation ||
                  parsedResponse?.translation ||
                  responseAssignment?.translation,
                assignments: responseAssignments,
                rawValue: inputResponse.value,
              });
              const hasMedia = Boolean(mediaUrl);
              const mediaExportValue = serializeMediaForExport({
                mediaUrl,
                transcription: transcriptionText,
                hasMedia,
              });

              if (transcriptionText) {
                formattedResponse = transcriptionText;
              } else if (hasMedia) {
                formattedResponse = UNTRANSCRIBED_VIDEO_VALUE;
              } else {
                formattedResponse = EMPTY_EXPORT_VALUE;
              }

              responseInputs.push({
                key: `${item._id}-${responseIndex}-${inputIndex}`,
                title: inputField.input_title || `Input ${inputIndex + 1}`,
                inputTitle: inputField.input_title || null,
                type: responseType,
                fieldType: inputField.input_type || null,
                inputId: inputField._id || null,
                userResponse: formattedResponse,
                sliderInfo: null,
                rawValue: inputResponse.value,
                inputOptions: fieldOptions,
                input_options: fieldOptions,
                slider_config: inputField.slider_config || null,
                mediaUrl,
                transcriptionText,
                translation:
                  inputResponse?.translation || responseAssignment?.translation,
                response_assignments: responseAssignments,
                exportValue: mediaExportValue,
              });
              return;
            }
            case "slider":
              const sliderValue = parseFloat(
                inputResponse.value.value || inputResponse.value,
              );

              if (!isNaN(sliderValue)) {
                const totalOptions =
                  inputField.input_options?.length ||
                  parseInt(inputField.scale_type || "5", 10);
                const showStars =
                  inputField.show_stars ||
                  inputField.slider_config?.show_stars ||
                  false;
                const zeroBasedOptions = Array.isArray(inputField.input_options)
                  ? inputField.input_options.every(
                      (opt, idx) => String(opt).trim() === String(idx),
                    )
                  : false;

                sliderInfo = {
                  value: sliderValue,
                  totalOptions,
                  inputOptions: inputField.input_options,
                  show_stars: showStars,
                };

                const emojiIndex = Math.round(
                  (sliderValue / 100) * (totalOptions - 1),
                );
                const starRating = emojiIndex + 1;
                const emojiData = getEmoji(emojiIndex, totalOptions);
                const descriptionsArray =
                  DEFAULT_EMOJI_DESCRIPTIONS[totalOptions] ||
                  DEFAULT_EMOJI_DESCRIPTIONS["5"];
                const description =
                  showStars || zeroBasedOptions
                    ? String(starRating)
                    : inputField.input_options?.[emojiIndex] ||
                      descriptionsArray[emojiIndex] ||
                      `Rating ${starRating}`;
                const displayValue =
                  showStars || zeroBasedOptions
                    ? `${starRating}/${totalOptions}`
                    : Math.round(sliderValue).toString() + "/100";

                formattedResponse = (
                  <div className="flex items-center justify-between w-full">
                    <div className="flex items-center">
                      <div className="mr-2">
                        <emojiData.Icon size={20} color={emojiData.color} />
                      </div>
                      <span>{description}</span>
                    </div>
                    <span className="ml-2 px-2 py-0.5 bg-gray-100 rounded-full text-xs font-medium">
                      {displayValue}
                    </span>
                  </div>
                );
              } else {
                formattedResponse = EMPTY_EXPORT_VALUE;
              }
              break;
            case "radio":
            case "dropdown":
              if (
                inputResponse.value &&
                typeof inputResponse.value === "object"
              ) {
                formattedResponse =
                  inputResponse.value.selection ||
                  inputResponse.value.value ||
                  inputResponse.value.otherValue ||
                  EMPTY_EXPORT_VALUE;
              } else {
                formattedResponse = inputResponse.value || EMPTY_EXPORT_VALUE;
              }
              break;
            case "checkbox-list":
            case "radio-list":
              formattedResponse =
                formatRadioListDisplayValue(inputResponse.value) ||
                EMPTY_EXPORT_VALUE;
              break;
            case "paragraph":
              if (typeof inputResponse.value === "string") {
                if (inputResponse.value.startsWith("http")) {
                  formattedResponse = EMPTY_EXPORT_VALUE;
                } else {
                  formattedResponse = inputResponse.value || EMPTY_EXPORT_VALUE;
                }
              } else {
                formattedResponse = EMPTY_EXPORT_VALUE;
              }
              break;
            case "date":
              if (
                inputResponse.value &&
                typeof inputResponse.value === "string"
              ) {
                try {
                  const dateObj = new Date(inputResponse.value);

                  if (!isNaN(dateObj.getTime())) {
                    formattedResponse = dateObj.toLocaleDateString(undefined, {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    });
                  } else {
                    formattedResponse = inputResponse.value;
                  }
                } catch (e) {
                  formattedResponse = inputResponse.value;
                }
              } else {
                formattedResponse = EMPTY_EXPORT_VALUE;
              }
              break;
            case "text":
            case "email":
            case "name":
            case "number":
              formattedResponse =
                typeof inputResponse.value === "object"
                  ? EMPTY_EXPORT_VALUE
                  : inputResponse.value || EMPTY_EXPORT_VALUE;
              break;
            default:
              formattedResponse =
                typeof inputResponse.value === "object"
                  ? EMPTY_EXPORT_VALUE
                  : inputResponse.value || EMPTY_EXPORT_VALUE;
          }

          if (
            isAnonymousExportInput(
              {
                title: inputField.input_title,
                inputTitle: inputField.input_title,
                type: responseType,
                fieldType: inputField.input_type,
              },
              formattedResponse,
              inputResponse.value,
            )
          ) {
            return;
          }

          responseInputs.push({
            key: `${item._id}-${responseIndex}-${inputIndex}`,
            title: inputField.input_title || `Input ${inputIndex + 1}`,
            inputTitle: inputField.input_title || null,
            type: responseType,
            fieldType: inputField.input_type || null,
            inputId: inputField._id || null,
            userResponse: formattedResponse,
            sliderInfo: sliderInfo,
            rawValue: inputResponse.value,
            file_type: inputField.file_type || inputField.fileType,
            inputOptions: fieldOptions,
            input_options: fieldOptions,
            slider_config: inputField.slider_config || null,
            show_stars:
              inputField.show_stars ||
              inputField.slider_config?.show_stars ||
              false,
            show_only_emojis:
              inputField.show_only_emojis ||
              inputField.slider_config?.show_only_emojis ||
              false,
            scale_type: inputField.scale_type,
            mediaUrl: isMediaInputType(inputField.input_type)
              ? extractMediaUrl(inputResponse.value)
              : null,
            transcriptionText: isMediaInputType(inputField.input_type)
              ? extractTranscriptionText({
                  transcription:
                    inputResponse?.transcriptionText ||
                    questionTranscriptionText,
                  translation:
                    inputResponse?.translation ||
                    responseAssignment?.translation,
                  assignments: responseAssignments,
                  rawValue: inputResponse.value,
                })
              : null,
            exportValue: getMatrixExportValue(inputField, inputResponse),
          });
        });

        if (responseInputs.length === 0) {
          return;
        }

        const hasAnyResponse = responseInputs.some(
          (input) =>
            input.userResponse !== EMPTY_EXPORT_VALUE &&
            input.userResponse !== "No response" &&
            input.userResponse !== "No video recorded",
        );

        if (!hasAnyResponse) {
          return;
        }

        const firstInput = responseInputs[0];
        const hasMultipleInputs = question.response.length > 1;

        responses.push({
          key: `${item._id}-${responseIndex}`,
          name: userName,
          email: userEmail,
          avatar,
          userId,
          avatarBackground,
          questionNumber: qNum,
          questionTitle: question.title || "N/A",
          type: hasMultipleInputs ? `` : firstInput.type || "Unknown",
          userResponse: hasMultipleInputs
            ? ``
            : firstInput.userResponse || EMPTY_EXPORT_VALUE,
          rawData: item,
          sliderInfo: hasMultipleInputs ? null : firstInput.sliderInfo || null,
          responseInputs,
          transcriptionText: firstInput.transcriptionText || null,
          translation: firstInput.translation || item.translation || null,
          response_assignments: responseAssignments,
        });
      } catch (e) {
        console.error("Failed to process response:", e);
        responses.push({
          key: `error-${responseIndex}`,
          name: "Error",
          email: "N/A",
          avatar: null,
          questionNumber: "N/A",
          questionTitle: "Error processing response",
          type: "Error",
          userResponse: "Processing Error",
          rawData: item,
        });
      }
    });

    return responses;
  };

  const parseReportData = () => {
    setLoading(true);

    try {
      const { reportData } = exportData;

      let flattenedReportData = [];

      if (reportData && Array.isArray(reportData)) {
        flattenedReportData = reportData.flatMap((userGroup) => {
          if (!userGroup.details || !Array.isArray(userGroup.details)) {
            return [];
          }

          const user =
            userGroup._id && typeof userGroup._id === "object"
              ? userGroup._id
              : { _id: userGroup._id };

          return userGroup.details.map((detail) => {
            const questionData = detail.questionData || detail.question;
            const questionId =
              questionData?._id ||
              (typeof detail.question === "string"
                ? detail.question
                : detail.question?._id);
            const pickAssignments = (...candidates) => {
              for (const candidate of candidates) {
                if (Array.isArray(candidate) && candidate.length > 0) {
                  return candidate;
                }
                if (
                  candidate &&
                  !Array.isArray(candidate) &&
                  typeof candidate === "object"
                ) {
                  return [candidate];
                }
              }
              return [];
            };
            const assignmentList = pickAssignments(
              detail.response_assignments,
              detail.assignments,
              detail.response_assignment,
              userGroup.response_assignments,
              userGroup.assignments,
            );
            const matchedAssignments = assignmentList.filter((assignment) => {
              if (!assignment || typeof assignment !== "object") return false;

              const assignmentQuestion =
                assignment.question?._id || assignment.question;

              if (!assignmentQuestion) return true;

              return (
                String(assignmentQuestion) === String(questionId) ||
                String(assignmentQuestion) === String(questionData?._id) ||
                String(assignmentQuestion) === String(questionData?.number)
              );
            });
            const assignments =
              matchedAssignments.length > 0
                ? matchedAssignments
                : assignmentList.length <= 1
                  ? assignmentList
                  : [];

            return {
              _id: detail._id || userGroup._id,
              question: questionData,
              questionId:
                questionData?._id ||
                (typeof detail.question === "string"
                  ? detail.question
                  : detail.question?._id),
              response: detail.response,
              response_assignments: assignments,
              translation:
                detail.translation ||
                detail.translations ||
                userGroup.translation ||
                null,
              transcriptionText: extractTranscriptionText({
                transcription:
                  questionData?.transcriptionText ||
                  detail.transcriptionText ||
                  userGroup.transcriptionText ||
                  null,
                translation:
                  detail.translation ||
                  detail.translations ||
                  userGroup.translation,
                assignments,
              }),
              user: user,
              step: detail.step,
              total_questions: detail.total_questions,
            };
          });
        });
      }

      const formQuestions = exportData?.formObj?.questions || [];
      const questionNumberMap =
        formQuestions.length > 0 ? buildQuestionNumberMap(formQuestions) : {};

      const formattedData = formatResponses(
        flattenedReportData,
        formQuestions.length > 0 ? questionNumberMap : null,
      );

      const respondentIdMap = buildRespondentPseudonymMap(formattedData);
      const dataWithRespondentIds = formattedData.map((row) => {
        const respondentKey = getInternalRespondentKey(row);
        const respondentId = respondentIdMap[respondentKey] || "User000";

        return {
          ...row,
          respondentId,
          respondentKey,
          name: respondentId,
        };
      });

      const sortedData = [...dataWithRespondentIds].sort((a, b) => {
        const respondentCompare = compareLocaleStrings(
          a.respondentId,
          b.respondentId,
        );

        if (respondentCompare !== 0) {
          return respondentCompare;
        }

        return compareNumericValues(a.questionNumber, b.questionNumber);
      });

      setTableData(sortedData);
    } catch (error) {
      console.error("Error parsing report data:", error);
      console.error("Report data structure:", exportData?.reportData);
      setTableData([]);
      message.error(
        "Failed to parse report data. Please check the console for details.",
      );
    } finally {
      setLoading(false);
    }
  };

  const downloadCsvFile = (rows, filename) =>
    new Promise((resolve) => {
      const escapeCell = (cell) => {
        if (cell == null) {
          return "";
        }

        const text = String(cell);

        if (/[",\n\r]/.test(text)) {
          return `"${text.replace(/"/g, '""')}"`;
        }

        return text;
      };

      const csvContent = (rows || [])
        .map((row) =>
          (Array.isArray(row) ? row : [row]).map(escapeCell).join(","),
        )
        .join("\n");
      const blob = new Blob(["\uFEFF" + csvContent], {
        type: "text/csv;charset=utf-8;",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.href = url;
      link.setAttribute("download", filename);
      link.style.display = "none";
      document.body.appendChild(link);
      link.click();

      // Keep the blob URL alive briefly — immediate revoke can cancel the download
      window.setTimeout(() => {
        if (link.parentNode) {
          document.body.removeChild(link);
        }
        URL.revokeObjectURL(url);
        resolve();
      }, 1000);
    });

  const handleExportCSV = async () => {
    // if (!canExportResponses) {
    //   notifyMissingTranscriptions();
    //   return;
    // }

    const formTitle = exportData?.formObj?.title || "form";
    const safeFormTitle = String(formTitle).replace(/\s+/g, "_");

    const { csvRows: responsesCsvRows } = buildExampleFormatCsvRows(
      tableData || [],
      formatResponseForCSV,
      null,
      exportData?.formObj?.questions || [],
    );

    await downloadCsvFile(responsesCsvRows, `${safeFormTitle}_responses.csv`);
  };

  const formatResponseForCSV = (response, sliderInfo) => {
    if (sliderInfo) {
      const { value, totalOptions, inputOptions, show_stars } = sliderInfo;
      const emojiIndex = Math.round((value / 100) * (totalOptions - 1));
      const starRating = emojiIndex + 1;
      const zeroBasedOptions = Array.isArray(inputOptions)
        ? inputOptions.every((opt, idx) => String(opt).trim() === String(idx))
        : false;

      if (show_stars || zeroBasedOptions) {
        return String(starRating);
      }

      const descriptionsArray =
        DEFAULT_EMOJI_DESCRIPTIONS[totalOptions] ||
        DEFAULT_EMOJI_DESCRIPTIONS["5"];
      const description =
        (Array.isArray(inputOptions) && inputOptions[emojiIndex]) ||
        descriptionsArray[emojiIndex] ||
        `Rating ${starRating}`;
      const displayValue = Math.round(value).toString() + "/100";

      return `${description} (${displayValue})`;
    }

    if (typeof response === "string") {
      if (isMediaStorageUrl(response)) {
        return UNTRANSCRIBED_VIDEO_VALUE;
      }

      const cleanedValue = cleanRatingValue(response);
      return capitalizeFirstLetter(cleanedValue);
    }

    if (React.isValidElement(response)) {
      if (
        response.type === "div" &&
        response.props.className?.includes("flex items-center justify-between")
      ) {
        const descriptionPart = response.props.children[0];
        const valuePart = response.props.children[1];

        let description = "";
        let value = "";

        // Extract description text (after the emoji)
        if (descriptionPart?.props?.children) {
          const descriptionChildren = descriptionPart.props.children;
          if (Array.isArray(descriptionChildren)) {
            const descSpan = descriptionChildren.find(
              (child) => typeof child === "object" && child.type === "span",
            );
            description = descSpan?.props?.children || "";
          }
        }

        // Extract the rating value
        if (valuePart?.props?.children) {
          value = valuePart.props.children;
          if (typeof value === "string") {
            value = value.replace(/,/g, "");
          }
        }

        return `${description} (${value})`;
      }

      // Handle video responses (div with flex class)
      if (
        response.type === "div" &&
        response.props.className?.includes("flex")
      ) {
        const children = response.props.children;

        if (Array.isArray(children)) {
          const translationElement = children.find(
            (child) => child && typeof child === "object" && child.type === "p",
          );

          if (translationElement) {
            const transcription = translationElement.props.children;
            return typeof transcription === "string" && transcription.trim()
              ? transcription.trim()
              : UNTRANSCRIBED_VIDEO_VALUE;
          }

          const placeholderChild = children.find(
            (child) =>
              typeof child === "string" && child.includes("untranscribed"),
          );
          if (placeholderChild) {
            return UNTRANSCRIBED_VIDEO_VALUE;
          }

          return UNTRANSCRIBED_VIDEO_VALUE;
        }

        return UNTRANSCRIBED_VIDEO_VALUE;
      }

      // Generic recursive function to extract text from React elements
      const extractTextContent = (element) => {
        if (!element || typeof element !== "object") {
          return element?.toString() || "";
        }

        if (typeof element === "string" || typeof element === "number") {
          return element.toString();
        }

        // If it's a React element with props.children
        if (element.props && element.props.children) {
          if (Array.isArray(element.props.children)) {
            return element.props.children
              .map((child) => extractTextContent(child))
              .filter((text) => text)
              .join(" ");
          } else {
            return extractTextContent(element.props.children);
          }
        }

        return "";
      };

      return extractTextContent(response);
    }

    if (typeof response === "object" && response !== null) {
      try {
        const checkboxValue = formatCheckboxExportValue(response);

        if (checkboxValue !== EMPTY_EXPORT_VALUE) {
          return checkboxValue;
        }

        if (response.value != null && typeof response.value === "object") {
          const nestedCheckboxValue = formatCheckboxExportValue(response.value);

          if (nestedCheckboxValue !== EMPTY_EXPORT_VALUE) {
            return nestedCheckboxValue;
          }

          const nestedValue = extractSliderNumericValue(response.value);

          if (nestedValue != null) {
            return String(Math.round(nestedValue));
          }

          return EMPTY_EXPORT_VALUE;
        }

        if (response.value != null && typeof response.value !== "object") {
          return String(response.value).replace(/,/g, "");
        }

        return EMPTY_EXPORT_VALUE;
      } catch (e) {
        return EMPTY_EXPORT_VALUE;
      }
    }

    // Fallback — never emit raw object strings
    if (typeof response === "object" && response !== null) {
      const mediaUrl = extractMediaUrl(response);
      const transcription = extractTranscriptionText({ rawValue: response });
      return serializeMediaForExport({
        mediaUrl,
        transcription,
        hasMedia: Boolean(mediaUrl || response),
      });
    }

    const fallback = String(response || "");
    return fallback === "[object Object]" ? EMPTY_EXPORT_VALUE : fallback;
  };

  const responsesLongFormatTableData = React.useMemo(
    () => buildLongFormatRecords(tableData, formatResponseForCSV),
    [tableData],
  );

  // Temporarily disabled: block export when record-video responses lack transcriptionText
  // const missingVideoTranscriptionCount = React.useMemo(
  //   () => countMissingRecordVideoTranscriptions(tableData || []),
  //   [tableData],
  // );
  // const canExportResponses = missingVideoTranscriptionCount === 0;
  // const notifyMissingTranscriptions = () => {
  //   message.error(
  //     missingVideoTranscriptionCount === 1
  //       ? "Cannot export: 1 video response is missing transcriptionText. Complete the transcription first."
  //       : `Cannot export: ${missingVideoTranscriptionCount} video responses are missing transcriptionText. Complete the transcriptions first.`,
  //   );
  // };
  const canExportResponses = true;

  const getUniqueQuestionData = () => {
    if (
      !exportData?.formObj?.questions ||
      !Array.isArray(exportData.formObj.questions)
    )
      return [];

    const questionNumberMap = buildQuestionNumberMap(
      exportData.formObj.questions,
    );

    const formatOptionsText = (responseField) => {
      if (responseField.input_type === "slider") {
        const scalePoints =
          responseField.slider_config?.scale_points ||
          parseInt(responseField.scale_type || "5", 10);

        let options = [];

        if (
          Array.isArray(responseField.input_options) &&
          responseField.input_options.length > 0
        ) {
          options = responseField.input_options;
        } else if (
          Array.isArray(responseField.slider_config?.options) &&
          responseField.slider_config.options.length > 0
        ) {
          options = responseField.slider_config.options;
        } else {
          options =
            DEFAULT_EMOJI_DESCRIPTIONS[String(scalePoints)] ||
            DEFAULT_EMOJI_DESCRIPTIONS["5"];
        }

        return options.join(", ");
      }

      if (Array.isArray(responseField.input_options)) {
        return responseField.input_options.join(", ");
      }

      return responseField.input_options || "";
    };

    return exportData.formObj.questions
      .filter((question) => question?.number != null)
      .sort((a, b) => Number(a.number) - Number(b.number))
      .map((question) => {
        const qNum = questionNumberMap[question.number] ?? question.number;
        const responseInputs = (question.response || []).map(
          (responseField, inputIndex) => ({
            key: `${question._id || question.number}-${responseField._id || inputIndex}`,
            title: responseField.input_title || `Input ${inputIndex + 1}`,
            inputTitle: responseField.input_title || null,
            type: responseField.input_type || "Text",
            input_type: responseField.input_type || "Text",
            input_options: responseField.input_options,
            scale_type: responseField.scale_type,
            slider_config: responseField.slider_config,
            show_emojis: responseField.show_emojis,
            show_only_emojis: responseField.show_only_emojis,
            show_stars:
              responseField.show_stars ||
              responseField.slider_config?.show_stars ||
              false,
            inputOptions: formatOptionsText(responseField),
          }),
        );

        const firstInput = responseInputs[0];
        const hasMultipleInputs = responseInputs.length > 1;

        return {
          key: `${question._id || question.number}`,
          questionNumber: qNum,
          questionTitle: question.title || "N/A",
          input_type: hasMultipleInputs ? "" : firstInput?.type || "Unknown",
          type: hasMultipleInputs ? "" : firstInput?.type || "Unknown",
          inputOptions: hasMultipleInputs ? "" : firstInput?.inputOptions || "",
          input_options: hasMultipleInputs
            ? []
            : firstInput?.input_options || [],
          scale_type: firstInput?.scale_type,
          slider_config: firstInput?.slider_config,
          show_emojis: firstInput?.show_emojis,
          show_only_emojis: firstInput?.show_only_emojis,
          show_stars: firstInput?.show_stars,
          responseInputs,
        };
      });
  };

  const questionGroupTableData = React.useMemo(
    () => getUniqueQuestionData(),
    [exportData?.formObj?.questions, tableData],
  );

  const questionMatrix = React.useMemo(() => {
    const choiceTypes = new Set(["checkbox", "radio", "dropdown", "select"]);
    const optionTypes = new Set([...choiceTypes, "slider"]);
    const columnsByKey = new Map();

    const normalizeOptionList = (input) => {
      const normalizedType = String(input.type || input.input_type || "")
        .trim()
        .toLowerCase();

      // Prefer raw options, then the formatted options string.
      // normalizeCheckboxOptions splits ["Yes, No, Maybe"] / "Yes, No, Maybe"
      // into separate option labels.
      let options = normalizeCheckboxOptions(
        input.input_options ??
          input.slider_config?.options ??
          input.inputOptions,
      );

      if (normalizedType === "slider" && options.length === 0) {
        const scalePoints =
          input.slider_config?.scale_points ||
          parseInt(input.scale_type || "5", 10);
        options =
          DEFAULT_EMOJI_DESCRIPTIONS[String(scalePoints)] ||
          DEFAULT_EMOJI_DESCRIPTIONS["5"];
      }

      if (
        normalizedType === "slider" &&
        (input.show_stars ||
          input.slider_config?.show_stars ||
          isZeroBasedScaleOptions(options))
      ) {
        const total =
          options.length ||
          Number(input.slider_config?.scale_points) ||
          parseInt(input.scale_type || "5", 10) ||
          5;

        return Array.from({ length: Math.max(total, 1) }, (_, index) =>
          String(index + 1),
        );
      }

      return options
        .map((option) => stringifyChoiceOption(option))
        .filter(Boolean);
    };

    const makeColumnKey = (value) =>
      String(stringifyChoiceOption(value) || value || "column")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "_")
        .replace(/^_+|_+$/g, "");

    const addMatrixColumn = (title) => {
      const key = makeColumnKey(title);

      if (!columnsByKey.has(key)) {
        columnsByKey.set(key, {
          title,
          dataIndex: key,
          key,
          width: 150,
          align: "center",
          ellipsis: { showTitle: false },
          sorter: (a, b) => compareLocaleStrings(a[key], b[key]),
          render: (value) => value || "",
        });
      }

      return key;
    };

    const rows = (questionGroupTableData || []).map((questionRow) => {
      const row = {
        key: questionRow.key,
        questionNumber: questionRow.questionNumber,
        question: questionRow.questionTitle || "N/A",
      };
      const typeCounts = {};

      (questionRow.responseInputs || []).forEach((input) => {
        const normalizedType = String(input.type || input.input_type || "")
          .trim()
          .toLowerCase();
        const typeLabel = capitalizeTypeFirstLetter(
          normalizedType.replace(/-/g, " "),
        );
        typeCounts[normalizedType] = (typeCounts[normalizedType] || 0) + 1;
        const typeIndex = typeCounts[normalizedType];
        const options = optionTypes.has(normalizedType)
          ? normalizeOptionList(input)
          : [];

        if (options.length > 0) {
          options.forEach((option) => {
            const columnKey = addMatrixColumn(
              `${typeLabel} (${typeIndex}) ${option}`,
            );
            row[columnKey] = "X";
          });

          return;
        }

        const columnKey = addMatrixColumn(typeLabel || "Unknown");
        row[columnKey] = "X";
      });

      return row;
    });

    return {
      columns: [
        {
          title: "Questions",
          dataIndex: "question",
          key: "question",
          width: 280,
          fixed: "left",
          ellipsis: { showTitle: false },
          sorter: (a, b) =>
            compareNumericValues(a.questionNumber, b.questionNumber),
        },
        ...columnsByKey.values(),
      ],
      rows,
    };
  }, [questionGroupTableData]);

  const questionResponseTableData = questionMatrix.rows;

  const responseMatrix = React.useMemo(() => {
    const optionTypes = new Set([
      "checkbox",
      "radio",
      "dropdown",
      "select",
      "slider",
    ]);

    const makeColumnKey = (value) =>
      String(stringifyChoiceOption(value) || value || "column")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "_")
        .replace(/^_+|_+$/g, "");

    const normalizeOptionList = (input) => {
      const normalizedType = String(input.type || input.input_type || "")
        .trim()
        .toLowerCase();
      let options = normalizeCheckboxOptions(
        input.input_options ??
          input.slider_config?.options ??
          input.inputOptions,
      );

      if (normalizedType === "slider" && options.length === 0) {
        const scalePoints =
          input.slider_config?.scale_points ||
          parseInt(input.scale_type || "5", 10);
        options =
          DEFAULT_EMOJI_DESCRIPTIONS[String(scalePoints)] ||
          DEFAULT_EMOJI_DESCRIPTIONS["5"];
      }

      if (
        normalizedType === "slider" &&
        (input.show_stars ||
          input.slider_config?.show_stars ||
          isZeroBasedScaleOptions(options))
      ) {
        const total =
          options.length ||
          Number(input.slider_config?.scale_points) ||
          parseInt(input.scale_type || "5", 10) ||
          5;

        return Array.from({ length: Math.max(total, 1) }, (_, index) =>
          String(index + 1),
        );
      }

      return options
        .map((option) => stringifyChoiceOption(option))
        .filter(Boolean);
    };

    const appendValue = (row, columnKey, value) => {
      if (!value || value === EMPTY_EXPORT_VALUE) return;

      if (!row[columnKey]) {
        row[columnKey] = value;
        return;
      }

      if (row[columnKey] !== value && !String(row[columnKey]).includes(value)) {
        row[columnKey] = `${row[columnKey]}, ${value}`;
      }
    };

    const getSimpleInputValue = (input) => {
      const normalizedType = String(
        input?.fieldType || input?.input_type || input?.type || "",
      )
        .trim()
        .toLowerCase();

      if (isRadioListInputType(normalizedType)) {
        const lines = getRadioListDisplayLines(
          input.rawValue ?? input.userResponse,
        );
        return lines.length ? lines.join("\n") : EMPTY_EXPORT_VALUE;
      }

      if (isMediaInputType(input?.type) || isMediaInputType(input?.fieldType)) {
        const transcriptionText =
          input.transcriptionText ||
          extractTranscriptionText({
            transcription: input.transcriptionText,
            translation: input.translation,
            assignments: input.response_assignments,
            rawValue: input.rawValue,
          });
        if (isUsableTranscriptionText(transcriptionText)) {
          return transcriptionText;
        }
        if (input.mediaUrl || extractMediaUrl(input.rawValue)) {
          return UNTRANSCRIBED_VIDEO_VALUE;
        }
        return EMPTY_EXPORT_VALUE;
      }

      if (input?.exportValue && input.exportValue !== EMPTY_EXPORT_VALUE) {
        return input.exportValue;
      }

      if (typeof input?.userResponse === "string") {
        if (
          !input.userResponse ||
          input.userResponse === "No response" ||
          input.userResponse === "No video recorded"
        ) {
          return EMPTY_EXPORT_VALUE;
        }
        return input.userResponse;
      }

      if (React.isValidElement(input?.userResponse)) {
        return formatResponseForCSV(input.userResponse, input.sliderInfo);
      }

      return EMPTY_EXPORT_VALUE;
    };

    const getSelectedOptionLabels = (input, options, normalizedType) => {
      if (normalizedType === "slider") {
        if (options.length === 0) return [];

        const raw = input.rawValue;

        // Free-text must never map onto a slider scale point.
        if (typeof raw === "string") {
          const trimmed = raw.trim();
          if (!trimmed) return [];

          const exact = options.find(
            (option) =>
              stringifyChoiceOption(option).toLowerCase() ===
              trimmed.toLowerCase(),
          );
          if (exact) return [exact];

          if (!/^\d+(\.\d+)?$/.test(trimmed)) {
            return [];
          }
        } else if (
          raw != null &&
          typeof raw !== "object" &&
          typeof raw !== "number"
        ) {
          return [];
        }

        const sliderValue = extractSliderNumericValue(raw);
        if (sliderValue == null) return [];

        const optionIndex = Math.round(
          (sliderValue / 100) * (options.length - 1),
        );
        const starRating = getSliderStarRating(raw, options.length);

        if (
          input.show_stars ||
          input.slider_config?.show_stars ||
          input.sliderInfo?.show_stars
        ) {
          return starRating != null ? [String(starRating)] : [];
        }

        return options[optionIndex] ? [options[optionIndex]] : [];
      }

      const { selections, otherValue } = getCheckboxSelections(
        input.rawValue != null ? input.rawValue : input.userResponse,
      );
      const selectedSet = new Set(
        selections
          .map((selection) => stringifyChoiceOption(selection).toLowerCase())
          .filter(Boolean),
      );

      if (otherValue) {
        selectedSet.add("other");
      }

      return options.filter((option) => {
        const normalizedOption = stringifyChoiceOption(option).toLowerCase();
        return (
          selectedSet.has(normalizedOption) ||
          (normalizedOption === "other" && Boolean(otherValue))
        );
      });
    };

    const rowsByRespondent = new Map();

    (tableData || []).forEach((responseRow) => {
      const respondentKey =
        responseRow.respondentKey || getInternalRespondentKey(responseRow);
      const respondentId =
        responseRow.respondentId || rowsByRespondent.get(respondentKey)?.user;

      if (!rowsByRespondent.has(respondentKey)) {
        rowsByRespondent.set(respondentKey, {
          key: respondentKey,
          user: respondentId,
          respondentId,
          email: responseRow.email,
          avatar: responseRow.avatar,
          userId: responseRow.userId,
          avatarBackground: responseRow.avatarBackground,
          respondentKey,
        });
      }

      const matrixRow = rowsByRespondent.get(respondentKey);
      const typeCounts = {};

      (responseRow.responseInputs || []).forEach((input) => {
        const normalizedType = String(
          input.fieldType || input.input_type || input.type || "",
        )
          .trim()
          .toLowerCase();
        const typeLabel = capitalizeTypeFirstLetter(
          normalizedType.replace(/-/g, " "),
        );
        typeCounts[normalizedType] = (typeCounts[normalizedType] || 0) + 1;
        const typeIndex = typeCounts[normalizedType];
        const options = optionTypes.has(normalizedType)
          ? normalizeOptionList(input)
          : [];

        if (options.length > 0) {
          const selectedOptions = getSelectedOptionLabels(
            input,
            options,
            normalizedType,
          );
          const selectedSet = new Set(
            selectedOptions.map((option) =>
              stringifyChoiceOption(option).toLowerCase(),
            ),
          );

          options.forEach((option) => {
            const columnKey = makeColumnKey(
              `${typeLabel} (${typeIndex}) ${option}`,
            );
            const isSelected = selectedSet.has(
              stringifyChoiceOption(option).toLowerCase(),
            );
            matrixRow[columnKey] = isSelected ? "✓" : EMPTY_EXPORT_VALUE;
          });

          return;
        }

        if (normalizedType === "file" || normalizedType === "multiple-files") {
          const columnKey = makeColumnKey(typeLabel || "Unknown");
          const nextValue = {
            kind: "files",
            value: input.rawValue ?? input.userResponse,
            fileTypeHint: input.file_type || input.fileType,
            text: getSimpleInputValue(input),
          };

          if (!matrixRow[columnKey] || matrixRow[columnKey] === EMPTY_EXPORT_VALUE) {
            matrixRow[columnKey] = nextValue;
          } else if (matrixRow[columnKey]?.kind === "files") {
            const existing = normalizeFileUrls(matrixRow[columnKey].value);
            const incoming = normalizeFileUrls(nextValue.value);
            matrixRow[columnKey] = {
              ...nextValue,
              value: [...existing, ...incoming],
              text: [...existing, ...incoming].join(", "),
            };
          } else {
            appendValue(matrixRow, columnKey, nextValue.text);
          }

          return;
        }

        const columnKey = makeColumnKey(typeLabel || "Unknown");
        appendValue(matrixRow, columnKey, getSimpleInputValue(input));
      });
    });

    const getMatrixCellText = (value) => {
      if (value && typeof value === "object" && value.kind === "files") {
        return value.text || "";
      }

      return value == null ? "" : String(value);
    };

    const renderMatrixAnswerCell = (value) => {
      if (value && typeof value === "object" && value.kind === "files") {
        return (
          <FileResponsePreview
            value={value.value}
            fileTypeHint={value.fileTypeHint}
            size="sm"
          />
        );
      }

      if (typeof value === "string" && value.includes("\n")) {
        return (
          <div className="whitespace-pre-line text-left leading-5">
            {value}
          </div>
        );
      }

      return value || EMPTY_EXPORT_VALUE;
    };

    const answerColumns = questionMatrix.columns.slice(1).map((column) => ({
      ...column,
      sorter: (a, b) =>
        compareLocaleStrings(
          getMatrixCellText(a[column.dataIndex]),
          getMatrixCellText(b[column.dataIndex]),
        ),
      render: renderMatrixAnswerCell,
    }));

    return {
      columns: [
        {
          title: "User",
          dataIndex: "user",
          key: "user",
          width: 220,
          fixed: "left",
          defaultSortOrder: "ascend",
          sorter: (a, b) => compareLocaleStrings(a.user, b.user),
          render: (text, record) => (
            <div className="flex items-center gap-3">
              <UserGradientAvatar
                src={record.avatar}
                alt={text}
                userId={record.userId || record.respondentKey}
                backgroundImageOverride={getUserAvatarBackgroundByIndex(
                  getRespondentLabelIndex(text || record.respondentId),
                )}
              />
              <div>
                <div className="font-medium">{text}</div>
                {record.email &&
                  record.email &&
                  record.email !== "N/A" &&
                  String(record.email).trim() && (
                    <div className="text-sm text-gray-500">{record.email}</div>
                  )}
              </div>
            </div>
          ),
        },
        ...answerColumns,
      ],
      rows: [...rowsByRespondent.values()].sort((a, b) =>
        compareLocaleStrings(a.user, b.user),
      ),
    };
  }, [questionMatrix.columns, tableData]);

  const addPdfPageFooters = (doc) => {
    const pageCount = doc.internal.getNumberOfPages();

    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(100, 100, 100);
      doc.text(
        `Generated by UNZIPPD - Page ${i} of ${pageCount}`,
        14,
        doc.internal.pageSize.height - 10,
      );
    }
  };

  const drawPdfReportHeader = (doc, formTitle, dateRange) => {
    const pageW = doc.internal.pageSize.getWidth();
    const rightPad = 14;
    const contentWidth = pageW - rightPad * 2;

    doc.setFontSize(18);
    doc.setTextColor(31, 41, 55);
    doc.setFont("helvetica", "bold");
    doc.text("All Responses Report", 14, 16);

    doc.setFontSize(14);
    doc.setTextColor(0, 0, 0);
    doc.setFont("helvetica", "bold");
    doc.text("UNZIPPD", pageW - rightPad, 16, { align: "right" });

    doc.setFillColor(243, 244, 246);
    doc.setDrawColor(243, 244, 246);
    const dateText = dateRange;
    const dateTextWidth =
      (doc.getStringUnitWidth(dateText) * 9) / doc.internal.scaleFactor;
    doc.roundedRect(14, 20, dateTextWidth + 10, 7, 3, 3, "FD");
    doc.setFontSize(9);
    doc.setTextColor(75, 85, 99);
    doc.setFont("helvetica", "normal");
    doc.text(dateText, 19, 25);

    doc.setDrawColor(243, 244, 246);
    doc.setFillColor(255, 255, 255);
    doc.roundedRect(14, 32, contentWidth, 26, 2, 2, "FD");

    doc.setFontSize(12);
    doc.setTextColor(31, 41, 55);
    doc.setFont("helvetica", "bold");
    const titleLines = doc.splitTextToSize(formTitle || "Form Report", contentWidth - 40);
    doc.text(titleLines, 20, 40);

    doc.setFontSize(9);
    doc.setTextColor(107, 114, 128);
    doc.setFont("helvetica", "normal");
    const description = exportData?.formObj?.description || "No description available";
    doc.text(doc.splitTextToSize(description, contentWidth - 40), 20, 46);

    let campaignText = `${exportData?.campaign || "All Campaigns"}`;

    if (exportData?.formObj?.organization) {
      campaignText += ` -> ${
        typeof exportData?.formObj?.organization === "string"
          ? "Organisation"
          : exportData?.formObj?.organization?.name || "Organisation"
      }`;
    }

    doc.text(doc.splitTextToSize(campaignText, contentWidth - 40), 20, 53);

    const status =
      exportData?.formObj?.status === "active" ? "Active" : "Inactive";
    const statusColor =
      exportData?.formObj?.status === "active"
        ? [250, 204, 21]
        : [156, 163, 175];
    const statusWidth = 24;
    const statusX = pageW - rightPad - statusWidth;
    doc.setFillColor(...statusColor);
    doc.roundedRect(statusX, 36, statusWidth, 6, 3, 3, "F");
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(8);
    const textWidth =
      (doc.getStringUnitWidth(status) * 8) / doc.internal.scaleFactor;
    doc.text(status, statusX + statusWidth / 2 - textWidth / 2, 40);
  };

  const handleExportPDF = async () => {
    try {
      const formTitle = exportData?.formObj?.title || "Form Report";
      const safeFormTitle = formTitle.replace(/\s+/g, "_");
      const dateRange = getDateRangeDisplay();
      const pageMargin = 10;
      const formQuestions = exportData?.formObj?.questions || [];
      const {
        headers: csvHeaders,
        bodyRows: csvBodyRows,
      } = buildExampleFormatCsvRows(
        tableData || [],
        formatResponseForCSV,
        null,
        formQuestions,
      );
      const csvIndex = {
        respondentId: csvHeaders.indexOf("respondent_id"),
        questionId: csvHeaders.indexOf("question_id"),
        question: csvHeaders.indexOf("question"),
        inputType: csvHeaders.indexOf("input_type"),
        responseOption: csvHeaders.indexOf("response_option"),
        response: csvHeaders.indexOf("response"),
      };
      const responsePdfHeaders = [
        "Respondent ID",
        "Input Type",
        "Question ID",
        "Question",
        "Response",
      ];
      // PDF keeps only answered rows and shows the selected value as Response.
      const sliderPdfConfigByQuestionId =
        buildSliderPdfConfigByQuestionId(formQuestions);
      const choiceLogoPdfConfigByQuestionId =
        buildChoiceLogoPdfConfigByQuestionId(formQuestions);
      const choiceLogoUrlByOption = buildChoiceLogoUrlByOption(
        choiceLogoPdfConfigByQuestionId,
      );
      const fileTypeHintByQuestionId = new Map();
      formQuestions.forEach((question) => {
        const key = `q${question.number}`.toLowerCase();
        (question.response || []).forEach((input) => {
          if (
            input?.input_type === "file" ||
            input?.input_type === "multiple-files"
          ) {
            if (!fileTypeHintByQuestionId.has(key)) {
              fileTypeHintByQuestionId.set(
                key,
                input.file_type || input.fileType,
              );
            }
          }
        });
      });
      const responseVisuals = [];
      const responsePdfRows = csvBodyRows
        .filter((row) => {
          const responseValue = row[csvIndex.response];
          const inputType = String(row[csvIndex.inputType] ?? "")
            .trim()
            .toLowerCase();

          if (
            responseValue == null ||
            String(responseValue).trim().length === 0
          ) {
            return false;
          }

          if (
            (inputType === "video" ||
              inputType === "audio" ||
              inputType === "media" ||
              inputType === "record-video") &&
            !isUsableTranscriptionText(responseValue)
          ) {
            return false;
          }

          if (
            inputType === "terms" ||
            inputType === "anonymous" ||
            isConsentReportField(row[csvIndex.question]) ||
            isAnonymousExportResponse(responseValue) ||
            isAnonymousExportResponse(row[csvIndex.responseOption])
          ) {
            return false;
          }

          return true;
        })
        .map((row) => {
          const responseValue = String(row[csvIndex.response] ?? "").trim();
          const optionValue = String(row[csvIndex.responseOption] ?? "").trim();
          const answer = responseValue || optionValue;
          const inputType = String(row[csvIndex.inputType] ?? "")
            .trim()
            .toLowerCase();
          const questionId = String(row[csvIndex.questionId] ?? "")
            .trim()
            .toLowerCase();

          let visual = null;
          if (inputType === "slider") {
            visual = resolveSliderPdfVisual(
              sliderPdfConfigByQuestionId.get(questionId),
              answer,
            );
          } else if (inputType === "radio" || inputType === "checkbox") {
            const optionLabel = optionValue || answer;
            visual = resolveChoiceLogoPdfVisual(
              choiceLogoPdfConfigByQuestionId.get(questionId),
              inputType,
              optionLabel,
              answer,
            );

            if (!visual) {
              const logoUrl = choiceLogoUrlByOption.get(
                String(optionLabel).trim().toLowerCase(),
              );
              if (logoUrl) {
                visual = {
                  type: "logo",
                  logoUrl,
                  label: answer,
                };
              }
            }
          } else if (inputType === "file" || inputType === "multiple-files") {
            const urls = getFileResponseUrls(answer);
            const hint = fileTypeHintByQuestionId.get(questionId);
            const imageUrls = urls.filter((url) => isImageFileUrl(url, hint));

            if (imageUrls.length) {
              visual = {
                type: "images",
                urls: imageUrls,
                extraText: urls
                  .filter((url) => !isImageFileUrl(url, hint))
                  .join(", "),
                fallbackText: answer,
              };
            }
          }

          responseVisuals.push(visual);

          const pdfAnswer = isRadioListInputType(inputType)
              ? formatRadioListDisplayValue(answer, "\n") || answer
              : answer;

          return [
            String(row[csvIndex.respondentId] ?? ""),
            formatExportInputTypeLabel(inputType),
            String(row[csvIndex.questionId] ?? ""),
            String(row[csvIndex.question] ?? ""),
            visual ? " " : pdfAnswer,
          ];
        });

      const logoUrlCache = new Map();
      const emojiRenderCache = new Map();
      const starsRenderCache = new Map();
      await Promise.all(
        responseVisuals.map(async (visual, index) => {
          if (!visual) return;

          if (visual.type === "stars") {
            const cacheKey = `${visual.filled || 0}/${visual.total || 0}`;
            let rendered = starsRenderCache.get(cacheKey);
            if (!rendered) {
              rendered = composeStarsPdfImage(visual.filled, visual.total);
              starsRenderCache.set(cacheKey, rendered);
            }

            if (!rendered) {
              responseVisuals[index] = null;
              responsePdfRows[index][4] = String(visual.filled || "");
              return;
            }

            visual.rendered = rendered;
            return;
          }

          if (visual.type === "emoji" && visual.symbol) {
            const cacheKey = `${visual.symbol}::${visual.label || ""}`;
            let rendered = emojiRenderCache.get(cacheKey);
            if (!rendered) {
              rendered = composeEmojiPdfImage(visual.symbol, visual.label);
              emojiRenderCache.set(cacheKey, rendered);
            }

            if (!rendered) {
              responseVisuals[index] = null;
              responsePdfRows[index][4] = visual.label || visual.symbol || "";
              return;
            }

            visual.rendered = rendered;
            return;
          }

          if (visual.type === "images") {
            const images = [];

            for (const url of visual.urls || []) {
              let dataUrl = logoUrlCache.get(url);
              if (dataUrl === undefined) {
                dataUrl = await loadImageAsDataUrl(url);
                logoUrlCache.set(url, dataUrl);
              }
              if (dataUrl) images.push(dataUrl);
            }

            if (!images.length) {
              responseVisuals[index] = null;
              responsePdfRows[index][4] =
                visual.extraText || visual.fallbackText || "";
              return;
            }

            visual.rendered = {
              images,
              extraText: visual.extraText || "",
            };
            return;
          }

          if (visual.type !== "logo" || !visual.logoUrl) return;

          let logoDataUrl = logoUrlCache.get(visual.logoUrl);
          if (logoDataUrl === undefined) {
            logoDataUrl = await loadImageAsDataUrl(visual.logoUrl);
            logoUrlCache.set(visual.logoUrl, logoDataUrl);
          }

          if (!logoDataUrl) {
            responseVisuals[index] = null;
            responsePdfRows[index][4] = visual.label || "";
            return;
          }

          const rendered = await composeLogoPdfImage(
            logoDataUrl,
            visual.label,
          );
          if (!rendered) {
            responseVisuals[index] = null;
            responsePdfRows[index][4] = visual.label || "";
            return;
          }

          visual.rendered = rendered;
        }),
      );

      const responseColumnCount = responsePdfHeaders.length;
      const doc = new jsPDF({
        orientation: "landscape",
        unit: "mm",
        format: "a4",
        compress: true,
      });
      const pageWidth = doc.internal.pageSize.getWidth();
      const tableWidth = pageWidth - pageMargin * 2;

      drawPdfReportHeader(doc, formTitle, dateRange);

      const statsStartX = 14;
      const statsStartY = 68;
      const statsGap = 3;
      const statsAvailableWidth = pageWidth - statsStartX * 2;
      const statsBoxWidth = (statsAvailableWidth - statsGap * 4) / 5;
      const statsLabels = [
        "Total Participants",
        "Response Received",
        "Response Ratio",
        "Total Questions",
        "Completion Rate",
      ];
      const statsValues = [
        statsData?.total || "0",
        statsData?.totalResponses || "0",
        getExportResponseRatio(statsData),
        statsData?.totalQuestions || statsData?.total || "0",
        formatRateToTwoDecimals(statsData?.complete ?? "0"),
      ];

      for (let i = 0; i < 5; i++) {
        const boxX = statsStartX + (statsBoxWidth + statsGap) * i;
        doc.setFillColor(249, 250, 251);
        doc.setDrawColor(249, 250, 251);
        doc.roundedRect(boxX, statsStartY, statsBoxWidth, 22, 2, 2, "F");
        doc.setFontSize(12);
        doc.setTextColor(0, 0, 0);
        doc.setFont("helvetica", "bold");
        doc.text(statsValues[i].toString(), boxX + 3, statsStartY + 9);
        doc.setFontSize(6.5);
        doc.setTextColor(107, 114, 128);
        doc.setFont("helvetica", "normal");
        doc.text(
          doc.splitTextToSize(statsLabels[i], statsBoxWidth - 4),
          boxX + 3,
          statsStartY + 15,
        );
      }

      const drawFullWidthTable = ({
        headers,
        bodyRows,
        columnCount,
        startY,
        firstColumnWidth,
      }) => {
        doc.autoTable({
          head: [headers],
          body: bodyRows,
          startY,
          tableWidth,
          pageBreak: "auto",
          rowPageBreak: "avoid",
          horizontalPageBreak: false,
          styles: {
            overflow: "linebreak",
            fontSize: 7,
            minCellHeight: 12,
            valign: "middle",
          },
          columnStyles: buildMatrixPdfColumnStyles(
            columnCount,
            pageWidth,
            pageMargin,
            { firstColumnWidth },
          ),
          headStyles: {
            fillColor: [243, 244, 246],
            textColor: [17, 24, 39],
            fontSize: 7,
            fontStyle: "bold",
            halign: "center",
            valign: "middle",
            cellPadding: { top: 3, right: 2, bottom: 3, left: 2 },
            minCellHeight: 18,
            overflow: "linebreak",
          },
          bodyStyles: {
            halign: "center",
            cellPadding: 2.5,
            fontSize: 7,
          },
          alternateRowStyles: {
            fillColor: [249, 250, 251],
          },
          margin: {
            top: 10,
            bottom: 12,
            left: pageMargin,
            right: pageMargin,
          },
          tableLineColor: [229, 231, 235],
          tableLineWidth: 0.4,
          showHead: "everyPage",
          didParseCell: (data) => {
            if (data.section === "body" && data.column.index === 0) {
              data.cell.styles.halign = "left";
              data.cell.styles.fontStyle = "bold";
              data.cell.styles.fontSize = 7;
            }
            if (data.section === "body" && data.column.index === 1) {
              data.cell.styles.halign = "left";
            }
            if (data.section === "body" && data.column.index === 3) {
              data.cell.styles.halign = "left";
            }
            if (data.section === "body" && data.column.index === 4) {
              data.cell.styles.halign = "left";
              const visual = responseVisuals[data.row.index];
              if (visual) {
                data.cell.text = [""];
                if (visual.type === "images") {
                  const count =
                    visual.rendered?.images?.length || visual.urls?.length || 1;
                  const perRow = Math.max(
                    1,
                    Math.floor(
                      (data.cell.width - 4) / (FILE_IMAGE_PDF_DRAW_MM + 2),
                    ),
                  );
                  const rows = Math.ceil(count / perRow);
                  data.cell.styles.minCellHeight =
                    rows * FILE_IMAGE_PDF_DRAW_MM + (rows - 1) * 2 + 4;
                } else if (visual.type === "logo") {
                  data.cell.styles.minCellHeight = 11;
                } else if (visual.type === "emoji") {
                  data.cell.styles.minCellHeight = 10;
                } else if (visual.type === "stars") {
                  data.cell.styles.minCellHeight = 10;
                } else {
                  data.cell.styles.minCellHeight = 12;
                }
              } else {
                const lines = Array.isArray(data.cell.text)
                  ? data.cell.text.filter(Boolean)
                  : String(data.cell.raw ?? "").split("\n");

                if (lines.length > 1) {
                  data.cell.styles.valign = "top";
                  data.cell.styles.minCellHeight = Math.max(
                    12,
                    lines.length * 4.8 + 4,
                  );
                }
              }
            }
            if (data.section === "head" && data.column.index === 0) {
              data.cell.styles.halign = "left";
              data.cell.styles.valign = "middle";
              data.cell.styles.fontSize = 7;
            }
          },
          didDrawCell: function (data) {
            if (data.section === "head") {
              const x = data.cell.x;
              const y = data.cell.y + data.cell.height;
              const width = data.cell.width;

              doc.setDrawColor(156, 163, 175);
              doc.setLineWidth(0.6);
              doc.line(x, y, x + width, y);
            }

            if (data.section === "body" && data.column.index === 4) {
              const visual = responseVisuals[data.row.index];
              if (!visual) return;

              if (visual.type === "images" && visual.rendered?.images?.length) {
                const images = visual.rendered.images;
                const gap = 2;
                const size = Math.min(
                  FILE_IMAGE_PDF_DRAW_MM,
                  Math.max(8, data.cell.height - 4),
                );
                let x = data.cell.x + 2;
                let y = data.cell.y + 2;
                const maxX = data.cell.x + data.cell.width - 2;

                images.forEach((dataUrl) => {
                  if (x + size > maxX && x > data.cell.x + 2) {
                    x = data.cell.x + 2;
                    y += size + gap;
                  }

                  try {
                    doc.addImage(
                      dataUrl,
                      "PNG",
                      x,
                      y,
                      size,
                      size,
                      undefined,
                      "NONE",
                    );
                  } catch {
                    // Skip images that jsPDF cannot embed.
                  }

                  x += size + gap;
                });
                return;
              }

              if (
                (visual.type === "logo" || visual.type === "emoji") &&
                visual.rendered?.dataUrl
              ) {
                const iconSize = Math.min(
                  visual.type === "emoji"
                    ? EMOJI_PDF_DRAW_MM
                    : LOGO_PDF_DRAW_MM,
                  data.cell.height - 2,
                );
                const iconX = data.cell.x + 2;
                const iconY =
                  data.cell.y + (data.cell.height - iconSize) / 2;

                doc.addImage(
                  visual.rendered.dataUrl,
                  "PNG",
                  iconX,
                  iconY,
                  iconSize,
                  iconSize,
                  undefined,
                  "NONE",
                );

                const label = String(
                  visual.rendered.label || visual.label || "",
                ).trim();
                if (label) {
                  doc.setFont("helvetica", "normal");
                  doc.setFontSize(7.5);
                  doc.setTextColor(17, 24, 39);
                  doc.text(
                    label,
                    iconX + iconSize + 2,
                    data.cell.y + data.cell.height / 2,
                    { baseline: "middle" },
                  );
                }
                return;
              }

              if (visual.type === "stars" && visual.rendered?.dataUrl) {
                const drawHeight = Math.min(
                  STAR_PDF_DRAW_MM,
                  data.cell.height - 2,
                );
                const aspect =
                  visual.rendered.width / Math.max(visual.rendered.height, 1);
                const drawWidth = Math.min(
                  data.cell.width - 4,
                  drawHeight * aspect,
                );

                doc.addImage(
                  visual.rendered.dataUrl,
                  "PNG",
                  data.cell.x + 2,
                  data.cell.y + (data.cell.height - drawHeight) / 2,
                  drawWidth,
                  drawHeight,
                  undefined,
                  "NONE",
                );
                return;
              }

              const image = renderPdfRatingVisual(visual);
              if (!image?.dataUrl) return;

              const maxHeight = Math.min(data.cell.height - 2, 8);
              const aspect = image.width / Math.max(image.height, 1);
              const drawHeight = maxHeight;
              const drawWidth = Math.min(
                data.cell.width - 4,
                drawHeight * aspect,
              );

              doc.addImage(
                image.dataUrl,
                "PNG",
                data.cell.x + 2,
                data.cell.y + (data.cell.height - drawHeight) / 2,
                drawWidth,
                drawHeight,
              );
            }
          },
        });
      };

      drawFullWidthTable({
        headers: responsePdfHeaders,
        bodyRows: responsePdfRows,
        columnCount: responseColumnCount,
        startY: statsStartY + 22,
        firstColumnWidth: 28,
      });

      addPdfPageFooters(doc);
      doc.save(`${safeFormTitle}_report.pdf`);
    } catch (error) {
      console.error("Error generating PDF:", error);
      message.error("Failed to generate PDF");
    }
  };

  // Helper function to remove commas but keep parentheses in ratings
  const cleanRatingValue = (value) => {
    if (typeof value !== "string") return value;

    // This pattern matches values like "3 (75,/100)" and removes only the commas
    return value
      .replace(/(\d+)\s*\(\s*(\d+),\s*\/100\s*\)/g, "$1 ($2/100)")
      .replace(/,\/100/g, "/100");
  };

  const renderUserResponseCell = (text, record) => {
    if (
      record?.type === "file" ||
      record?.type === "multiple-files" ||
      record?.fieldType === "file" ||
      record?.fieldType === "multiple-files"
    ) {
      return (
        <FileResponsePreview
          value={record.rawValue ?? record.userResponse ?? text}
          fileTypeHint={record.file_type || record.fileType}
        />
      );
    }

    if (isMediaInputType(record?.type) || isMediaInputType(record?.fieldType)) {
      const transcriptionText = extractTranscriptionText({
        transcription: record.transcriptionText,
        translation: record.translation,
        assignments: record.response_assignments,
        rawValue: record.rawValue,
      });

      if (isUsableTranscriptionText(transcriptionText)) {
        return transcriptionText;
      }

      const hasMedia =
        Boolean(record.mediaUrl) || Boolean(extractMediaUrl(record.rawValue));

      return hasMedia ? UNTRANSCRIBED_VIDEO_VALUE : EMPTY_EXPORT_VALUE;
    }

    if (React.isValidElement(text)) {
      return text;
    }

    if (React.isValidElement(record?.userResponse)) {
      return record.userResponse;
    }

    if (
      text == null ||
      text === "" ||
      text === "No response" ||
      text === "No video recorded" ||
      text === EMPTY_EXPORT_VALUE
    ) {
      return EMPTY_EXPORT_VALUE;
    }

    if (
      isRadioListInputType(record?.type) ||
      isRadioListInputType(record?.fieldType)
    ) {
      const lines = getRadioListDisplayLines(
        record.rawValue ?? record.userResponse ?? text,
      );

      if (!lines.length) {
        return EMPTY_EXPORT_VALUE;
      }

      return (
        <div className="whitespace-pre-line text-left leading-5">
          {lines.join("\n")}
        </div>
      );
    }

    if (
      record?.type === "checkbox" ||
      record?.type === "radio" ||
      record?.type === "dropdown"
    ) {
      const choiceValue = formatCheckboxExportValue(
        record.rawValue != null ? record.rawValue : text,
      );
      return choiceValue === EMPTY_EXPORT_VALUE
        ? EMPTY_EXPORT_VALUE
        : capitalizeFirstLetter(choiceValue);
    }

    if (typeof text === "string" && text.includes("(") && text.includes(")")) {
      const [description, value] = text.split(" (");

      return (
        <div>
          <div>{capitalizeFirstLetter(description)}</div>
          <div className="text-xs text-gray-500">{value.replace(")", "")}</div>
        </div>
      );
    }

    if (typeof text === "object" && text !== null) {
      const checkboxValue = formatCheckboxExportValue(text);
      return checkboxValue === EMPTY_EXPORT_VALUE
        ? EMPTY_EXPORT_VALUE
        : capitalizeFirstLetter(checkboxValue);
    }

    return typeof text === "string" ? capitalizeFirstLetter(text) : text;
  };

  const columns = [
    {
      title: "User",
      dataIndex: "respondentId",
      key: "respondentId",
      width: 220,
      defaultSortOrder: "ascend",
      sorter: (a, b) => compareLongFormatRecords(a, b),
      render: (text, record) => {
        const user = record.rawData?.user;
        const displayName =
          record.respondentId || text || getUserDisplayName(user);
        const displayAvatar = user?.image || user?.avatar || record.avatar;
        const displayEmail = record.email;

        return (
          <div className="flex items-center gap-3">
            <UserGradientAvatar
              src={displayAvatar}
              alt={displayName}
              userId={user?._id || record.userId || record.respondentKey}
              backgroundImageOverride={getUserAvatarBackgroundByIndex(
                getRespondentLabelIndex(displayName),
              )}
            />
            <div>
              <div className="font-medium">{displayName}</div>
              {displayEmail &&
                displayEmail &&
                displayEmail !== "N/A" &&
                String(displayEmail).trim() && (
                  <div className="text-sm text-gray-500">{displayEmail}</div>
                )}
            </div>
          </div>
        );
      },
    },
    {
      title: "Question",
      dataIndex: "question",
      key: "question",
      width: 140,
      sorter: (a, b) =>
        compareNumericValues(a.questionNumber, b.questionNumber),
    },
    {
      title: "Input field name",
      dataIndex: "subquestion",
      key: "subquestion",
      ellipsis: { showTitle: false },
      sorter: (a, b) => {
        const questionCompare = compareNumericValues(
          a.questionNumber,
          b.questionNumber,
        );
        if (questionCompare !== 0) return questionCompare;
        return compareNumericValues(a.inputIndex, b.inputIndex);
      },
      render: (text) =>
        !text || text === EMPTY_EXPORT_VALUE
          ? EMPTY_EXPORT_VALUE
          : capitalizeFirstLetter(text),
    },
    {
      title: "Response",
      dataIndex: "response",
      key: "response",
      ellipsis: { showTitle: false },
      sorter: (a, b) => compareLocaleStrings(a.response, b.response),
      render: (text, record) =>
        renderUserResponseCell(record.userResponse ?? text, record),
    },
  ];

  const getDateRangeDisplay = () => {
    if (!exportData?.dateRange) {
      return "All Time";
    }

    const { startDate, endDate } = exportData.dateRange;

    if (startDate && endDate) {
      return `${startDate} — ${endDate}`;
    } else if (startDate) {
      return `From ${startDate}`;
    } else if (endDate) {
      return `Until ${endDate}`;
    }

    return "All Time";
  };

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      width="100%"
      centered
      footer={null}
      className="reports-modal "
      style={{ maxWidth: "1200px" }}
    >
      {loading || isLoadingStats ? (
        <div className="flex justify-center items-center h-64">
          <Spin size="large" />
        </div>
      ) : (
        <div className="pt-8">
          <div
            style={{
              maxHeight: "85vh",
              overflow: "auto",
            }}
          >
            <div className="border-b pb-6 mb-6">
              <div className="flex flex-col sm:flex-row justify-between items-start mb-6">
                <div className="flex flex-wrap items-center gap-3 mb-4 sm:mb-0">
                  <h2 className="text-2xl sm:text-3xl font-semibold text-gray-900 break-words m-0">
                    All Responses Report{" "}
                  </h2>
                  <div className="bg-gray-100 px-4 py-1 rounded-full text-sm text-gray-600">
                    {getDateRangeDisplay()}
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-6 w-full sm:w-auto">
                  {/* <span className="text-2xl font-bold tracking-widest px-4">
                    UNZIPPD
                  </span> */}
                  <div className="flex flex-col items-end gap-2 w-full sm:w-auto">
                    <div className="flex gap-2 w-full sm:w-auto">
                      <Button
                        type="primary"
                        icon={<FilePdfOutlined />}
                        onClick={handleExportPDF}
                        disabled={!canExportResponses}
                        className="bg-red-600 hover:bg-red-700 disabled:opacity-50"
                      >
                        Download PDF
                      </Button>
                      <Button
                        type="primary"
                        icon={<DownloadOutlined />}
                        onClick={handleExportCSV}
                        disabled={!canExportResponses}
                        className="bg-[#18181B] hover:bg-[#18181B]/90 disabled:opacity-50"
                      >
                        Download CSV
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-2xl border border-gray-100 p-4 sm:p-6">
                <div className="flex flex-col sm:flex-row justify-between items-start gap-3">
                  <div className="w-full">
                    <h3 className="text-xl font-semibold mb-2 break-words">
                      {exportData?.formObj?.title || "Form Report"}
                    </h3>
                    <p className="text-gray-500 text-sm mb-4 break-words">
                      {exportData?.formObj?.description ||
                        "No description available"}
                    </p>
                    <div className="flex flex-wrap items-center gap-2 text-sm">
                      <span className="font-medium">
                        {exportData?.campaign || "All Campaigns"}
                      </span>
                      {exportData?.formObj?.organization && (
                        <>
                          <span className="text-gray-400">→</span>
                          <span>
                            {exportData?.formObj?.organization?.name ||
                              (typeof exportData?.formObj?.organization ===
                              "string"
                                ? "Organisation "
                                : "Organisation ")}
                          </span>
                          <span className="text-gray-400 mx-2">•</span>
                        </>
                      )}
                      <span className="text-gray-600">
                        {exportData?.formObj?.start_date &&
                        exportData?.formObj?.end_date
                          ? `${new Date(
                              exportData.formObj.start_date,
                            ).toLocaleDateString()} - ${new Date(
                              exportData.formObj.end_date,
                            ).toLocaleDateString()}`
                          : getDateRangeDisplay()}
                      </span>
                    </div>
                  </div>
                  <div
                    className={`${
                      exportData?.formObj?.status === "active"
                        ? "bg-yellow-400"
                        : "bg-gray-400"
                    } px-3 py-1 rounded-full text-sm font-medium shrink-0`}
                  >
                    {exportData?.formObj?.status === "active"
                      ? "Active"
                      : "Inactive"}
                  </div>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-8">
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="text-2xl sm:text-3xl font-bold">
                  {statsData?.total}
                </div>
                <div className="text-sm text-gray-500">Total Participants</div>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="text-2xl sm:text-3xl font-bold">
                  {statsData?.totalResponses}
                </div>
                <div className="text-sm text-gray-500">Response Received</div>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="text-2xl sm:text-3xl font-bold">
                  {getExportResponseRatio(statsData)}
                </div>
                <div className="text-sm text-gray-500">Response Ratio</div>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="text-2xl sm:text-3xl font-bold">
                  {statsData?.totalQuestions || statsData?.total || 0}
                </div>
                <div className="text-sm text-gray-500">Total Questions</div>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="text-2xl sm:text-3xl font-bold">
                  {formatRateToTwoDecimals(statsData?.complete)}
                </div>
                <div className="text-sm text-gray-500">Completion Rate</div>
              </div>
            </div>
            <div className="mb-8 overflow-auto">
              <h3 className="mb-3 text-lg font-semibold text-gray-900">
                Questions
              </h3>
              <Table
                scroll={{ x: true }}
                tableLayout="fixed"
                columns={questionMatrix.columns}
                dataSource={questionResponseTableData}
                pagination={buildConditionalPagination(
                  questionResponseTableData,
                )}
                rowKey="key"
                className="reports-table"
                showSorterTooltip={false}
                locale={{
                  emptyText: "No responses found",
                }}
              />
            </div>
            <div className="overflow-auto">
              <h3 className="mb-3 text-lg font-semibold text-gray-900">
                User Responses
              </h3>
              <Table
                columns={responseMatrix.columns}
                dataSource={responseMatrix.rows}
                scroll={{ x: true }}
                tableLayout="fixed"
                pagination={buildConditionalPagination(responseMatrix.rows)}
                rowKey="key"
                className="reports-table"
                showSorterTooltip={false}
                locale={{
                  emptyText: "No responses found",
                }}
              />
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default ExportReportFormsModal;
